import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  IconButton
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import ClearIcon from "@mui/icons-material/Clear";
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import SupportIcon from '@mui/icons-material/Support';
import axios from 'axios';
import Swal from 'sweetalert2';
import { generateApiKey } from "../../../../helpers/common.helpers";
import api from "../../../../services/server";

const SUGGESTION_CATEGORIES = [
  { value: "dashboard", label: "Dashboard" },
  { value: "lms", label: "LMS / Learning Portal" },
  { value: "course_content", label: "Course Content & Curriculum" },
  { value: "learning_resources", label: "Learning Resources & Recordings" },
  { value: "live_sessions", label: "Live Sessions" },
  { value: "faculty_accredian", label: "Faculty / Accredian Team" },
  { value: "assessments_quizzes", label: "Assessments & Quizzes" },
  { value: "projects_assignments", label: "Projects & Assignments" },
  { value: "community_networking", label: "Community & Networking" },
  { value: "ai_features", label: "AI Features & Tools" },
  { value: "others", label: "Others" },
];

const CONCERN_CATEGORIES = [
  { value: "platform", label: "Platform" },
  { value: "attendance", label: "Attendance (Tracking/ Visibility)" },
  { value: "learning_experience", label: "Learning Experience" },
  { value: "course_content", label: "Course Content" },
  { value: "faculty_accredian", label: "Faculty / Accredian Team" },
  { value: "live_sessions", label: "Live Sessions" },
  { value: "resources_recordings", label: "Resources & Recordings" },
  { value: "assignments_projects", label: "Assignments & Projects" },
  { value: "others", label: "Others" },
];

const APPRECIATION_CATEGORIES = [
  { value: "faculty", label: "Faculty" },
  { value: "accredian_team", label: "Accredian Team" },
  { value: "product_platform", label: "Product & Platform" },
  { value: "overall_learning", label: "Overall Learning Experience" },
  { value: "others", label: "Others" }
];

const ShareFeedback = ({ open, setOpen }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shareFeedback, setShareFeedback] = useState("");

  // New state variables
  const [feedbackType, setFeedbackType] = useState("suggestion");
  const [category, setCategory] = useState("");
  const [feedback, setFeedback] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [otherFeedback, setOtherFeedback] = useState("");
  const [attachmentError, setAttachmentError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [concernOption, setConcernOption] = useState("");

  const handleResetForm = () => {
    setCategory("");
    setFeedback("");
    setAdditionalInfo("");
    setAttachment(null);
    setOtherFeedback("");
    setAttachmentError("");
    setIsSubmitted(false);
    setConcernOption("");
  };

  const submitForm = async (e) => {
    setIsLoading(true);
    e.preventDefault();

    const selectedOption = currentOptions.find(opt => opt.value === category);
    const categoryLabel = selectedOption ? selectedOption.label : category;

    const formData = new FormData();
    formData.append("enrol_id", localStorage.getItem("enrol_id"));
    formData.append("feedback_type", feedbackType);
    formData.append("category", categoryLabel);
    formData.append("feedback", feedback);
    if (feedbackType === 'suggestion' && additionalInfo) {
      formData.append("additional_info", additionalInfo);
    }
    if (category === 'others' && otherFeedback) {
      formData.append("other_feedback", otherFeedback);
    }
    if (attachment) {
      formData.append("attachment", attachment);
    }

    try {
      const result = await api.post("/students/submit-feedback", formData);

      if (result.status === 200 || result.data.success) {
        setIsSubmitted(true);
      } else {
        handleClose();
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      Swal.fire({
        title: "Error",
        text: "Could not submit feedback. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleModal = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios({
        method: "post",
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "page-index": generateApiKey()
        },
        url: `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/headerdetails/fetch_share_feedback_details`,
        data: {
          enrol_id: localStorage.getItem("enrol_id"),
        },
      });
      setShareFeedback(res.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching feedback details:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open === "share-feedback") {
      handleModal();
    }
  }, [open, handleModal]);

  const handleClose = () => {
    setOpen(null);
    setIsModalOpen(false);
    // Reset state
    setFeedbackType("suggestion");
    setCategory("");
    setFeedback("");
    setAdditionalInfo("");
    setAttachment(null);
    setOtherFeedback("");
    setAttachmentError("");
    setIsSubmitted(false);
    setConcernOption("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setAttachmentError("");
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setAttachmentError("Maximum file size is 5MB.");
        setAttachment(null);
        return;
      }
      const allowedExtensions = ["webp", "jpeg", "jpg", "pdf"];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(fileExtension)) {
        setAttachmentError("Only WEBP, JPEG, JPG, and PDF files are allowed.");
        setAttachment(null);
        return;
      }
      setAttachment(file);
    }
  };

  const feedbackTypes = [
    { value: 'suggestion', label: 'Suggestion', icon: <LightbulbOutlinedIcon sx={{ mr: 1, fontSize: '20px' }} /> },
    { value: 'concern', label: 'Concern', icon: <ErrorOutlineIcon sx={{ mr: 1, fontSize: '20px' }} /> },
    { value: 'appreciation', label: 'Appreciation', icon: <FavoriteBorderIcon sx={{ mr: 1, fontSize: '20px' }} /> },
  ];

  let selectLabel = '';
  let placeholder = '';
  let currentOptions = [];

  if (feedbackType === 'suggestion') {
    selectLabel = 'Area of Improvement';
    placeholder = 'Select an area';
    currentOptions = SUGGESTION_CATEGORIES;
  } else if (feedbackType === 'concern') {
    selectLabel = 'Concern';
    placeholder = 'Select a concern';
    currentOptions = CONCERN_CATEGORIES;
  } else {
    selectLabel = 'Appreciation';
    placeholder = 'Select who or what to appreciate';
    currentOptions = APPRECIATION_CATEGORIES;
  }

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        "& .MuiDialog-paper": {
          width: "600px",
          borderRadius: "12px",
        },
      }}
    >
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {isSubmitted ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: 4, pt: 5, pb: 4, position: 'relative' }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: '#9CA3AF',
              p: 0.5,
            }}
          >
            <ClearIcon />
          </IconButton>

          {/* Green Check Circle */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#DEF7EC',
              color: '#0E9F6E',
              mb: 3,
            }}
          >
            <CheckIcon sx={{ fontSize: '32px' }} />
          </Box>

          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#111827',
              fontSize: '1.25rem',
              mb: 1.5,
            }}
          >
            Thank You For Your Feedback
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              color: '#4B5563',
              lineHeight: 1.6,
              fontSize: '0.875rem',
              mb: 4,
              maxWidth: '460px',
            }}
          >
            Your feedback has been successfully shared with our team. We regularly review learner suggestions, concerns, and appreciation to improve the overall learning experience. Thank you for helping us build a better platform.
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
                borderRadius: '8px',
                fontWeight: '600',
                px: 4,
                py: 1.25,
                '&:hover': {
                  borderColor: '#9CA3AF',
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleResetForm}
              sx={{
                textTransform: 'none',
                backgroundColor: '#3B82F6',
                color: '#FFFFFF',
                borderRadius: '8px',
                fontWeight: '600',
                px: 3,
                py: 1.25,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#2563EB',
                  boxShadow: 'none',
                },
              }}
            >
              Submit Another Feedback
            </Button>
          </Box>
        </Box>
      ) : (
        <form onSubmit={submitForm}>
          {/* Header */}
          <DialogTitle sx={{ m: 0, p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.25rem' }}>
                Share Feedback
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                Help us improve your learning experience.
              </Typography>
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: '#9CA3AF',
                p: 0.5,
                mt: -0.5,
                mr: -0.5,
              }}
            >
              <ClearIcon />
            </IconButton>
          </DialogTitle>

          <div style={{ borderBottom: '1px solid #E5E7EB', margin: '0 24px' }}></div>

          {/* Content */}
          <DialogContent sx={{ p: 3, pt: 2.5 }}>
            {/* Feedback Type Tabs */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: '600', mb: 1, color: '#374151' }}>
                Feedback Type <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <Box
  sx={{
    display: 'flex',
    flexDirection: 'row',
    gap: { xs: 0.75, sm: 2 },
    width: '100%',
    minWidth: 0,
  }}
>
                {feedbackTypes.map((type) => {
                  const isActive = feedbackType === type.value;
                  return (
                    <Button
                      key={type.value}
                      variant="outlined"
                      onClick={() => {
                        setFeedbackType(type.value);
                        setCategory('');
                        setFeedback('');
                        setAdditionalInfo('');
                        setAttachment(null);
                        setOtherFeedback('');
                        setConcernOption('');
                      }}
                      sx={{
  flex: '1 1 0',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: { xs: 0, sm: 1 },
  textTransform: 'none',
  borderRadius: '8px',
  py: 1.25,
  px: { xs: 1, sm: 2 },
  whiteSpace: 'nowrap',
  fontSize: { xs: '0.7rem', sm: '0.875rem' },
  border: isActive ? '2px solid #4F46E5' : '1px solid #D1D5DB',
  borderColor: isActive ? '#4F46E5' : '#D1D5DB',
  color: isActive ? '#4F46E5' : '#374151',
  backgroundColor: isActive ? '#EEF2FF' : 'transparent',
  fontWeight: isActive ? 600 : 500,
  '& .MuiSvgIcon-root': {
    flexShrink: 0,
    fontSize: { xs: '14px', sm: '20px' },
  },
  '&:hover': {
    borderColor: isActive ? '#4338CA' : '#9CA3AF',
    backgroundColor: isActive ? '#E0E7FF' : '#F9FAFB',
  },
}}
                    >
                      {type.icon}
                      {type.label}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {/* Get Support Option (Concern Only) */}
            {feedbackType === 'concern' && (
              <Box>
                {/* Get Support Card */}
                <Box
                  onClick={() => setConcernOption('support')}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    border: concernOption === 'support' ? '2px solid #4F46E5' : '1px solid #E5E7EB',
                    borderRadius: '12px',
                    p: 2.5,
                    cursor: 'pointer',
                    backgroundColor: concernOption === 'support' ? '#EEF2FF' : '#FFFFFF',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: concernOption === 'support' ? '#4F46E5' : '#D1D5DB',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SupportIcon
                      sx={{
                        color: concernOption === 'support' ? '#4F46E5' : '#4B5563',
                        fontSize: '20px',
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: '700',
                        color: concernOption === 'support' ? '#4F46E5' : '#1F2937',
                        fontSize: '0.9rem',
                      }}
                    >
                      Get Support
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: concernOption === 'support' ? '#6366F1' : '#6B7280',
                      fontSize: '0.8rem',
                      lineHeight: 1.4,
                      textAlign: 'left'
                    }}
                  >
                    Get help resolving a specific issue.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Support Yellow Box (Get Support selected) */}
            {feedbackType === 'concern' && concernOption === 'support' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  backgroundColor: '#FFFDF0',
                  border: '1px solid #FDE8C3',
                  borderRadius: '8px',
                  padding: '16px',
                  mt: 2,
                  gap: 1.5,
                }}
              >
                <SupportIcon sx={{ color: '#D97706', mt: '2px', fontSize: '20px' }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: '700', color: '#92400E', fontSize: '0.875rem' }}>
                    Need help with this concern?
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B45309', mt: 0.5, mb: 1, fontSize: '0.85rem', lineHeight: '1.4' }}>
                    It looks like your concern requires assistance from our support team. Raise a support ticket to receive updates and track the resolution of your issue.
                  </Typography>
                  <a
                    href="/support"
                    onClick={(e) => {
                      e.preventDefault();
                      if (window.FreshworksWidget) {
                        window.FreshworksWidget('show', 'launcher');
                        window.FreshworksWidget('open');
                      }
                    }}
                    style={{
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: '#4F46E5',
                      fontWeight: '700',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    Raise a Support Ticket <ArrowForwardIcon sx={{ ml: 0.5, fontSize: '14px' }} />
                  </a>
                </Box>
              </Box>
            )}

            {/* Regular Form Fields (Suggestion, Appreciation) */}
            {feedbackType !== 'concern' && (
              <>
                {/* Category Dropdown */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: '600', mb: 1, color: '#374151' }}>
                    {selectLabel} <span style={{ color: '#EF4444' }}>*</span>
                  </Typography>
                  <TextField
                    select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (!selected) {
                          return <span style={{ color: '#9CA3AF' }}>{placeholder}</span>;
                        }
                        const found = currentOptions.find(opt => opt.value === selected);
                        return found ? found.label : selected;
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#FFFFFF',
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      {placeholder}
                    </MenuItem>
                    {currentOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Other Feedback Input */}
                {category === "others" && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: '600', mb: 1, color: '#374151' }}>
                      Please specify <span style={{ color: '#EF4444' }}>*</span>
                    </Typography>
                    <TextField
                      id="other_feedback"
                      variant="outlined"
                      placeholder="Please describe what this is about"
                      required
                      fullWidth
                      value={otherFeedback}
                      onChange={(e) => setOtherFeedback(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                        }
                      }}
                    />
                  </Box>
                )}

                {/* Main Feedback Textarea */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: '600', color: '#374151' }}>
                      {feedbackType === 'suggestion' ? 'Your Suggestion' : feedbackType === 'concern' ? 'Describe Your Concern' : 'Share Your Appreciation'} <span style={{ color: '#EF4444' }}>*</span>
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: '500' }}>
                      {feedback.length}/2000
                    </Typography>
                  </Box>
                  <TextField
                    multiline
                    rows={4}
                    placeholder={
                      feedbackType === 'suggestion'
                        ? "What would you like us to improve, add, or change?"
                        : feedbackType === 'concern'
                          ? "Tell us what could be improved or what isn't meeting your expectations."
                          : "Tell us what you appreciated and how it positively impacted your experience."
                    }
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value.slice(0, 2000))}
                    fullWidth
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      }
                    }}
                  />
                </Box>

                {/* Secondary Textarea (Suggestion Only) */}
                {feedbackType === 'suggestion' && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: '600', mb: 1, color: '#374151' }}>
                      How would this help you? (Optional)
                    </Typography>
                    <TextField
                      multiline
                      rows={3}
                      placeholder="Tell us how this improvement would enhance your learning experience."
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                        }
                      }}
                    />
                  </Box>
                )}

                {/* Attachment Row (Suggestion & Concern Only) */}
                {(feedbackType === 'suggestion' || feedbackType === 'concern') && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<AttachFileIcon />}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#D1D5DB',
                          color: '#374151',
                          borderRadius: '8px',
                          fontWeight: '500',
                          px: 2,
                          py: 1,
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            borderColor: '#9CA3AF',
                            backgroundColor: '#F3F4F6',
                          },
                        }}
                      >
                        <span style={{ whiteSpace: 'nowrap' }}>Attach file</span>
                        <input
                          type="file"
                          hidden
                          accept=".webp,.jpg,.jpeg,.pdf"
                          onChange={handleFileChange}
                        />
                      </Button>
                      {attachment ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#374151', 
                              fontWeight: '500',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                              flex: 1
                            }}
                            title={`${attachment.name} (${(attachment.size / (1024 * 1024)).toFixed(2)} MB)`}
                          >
                            {attachment.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6B7280', flexShrink: 0, whiteSpace: 'nowrap' }}>
                            ({(attachment.size / (1024 * 1024)).toFixed(2)} MB)
                          </Typography>
                          <IconButton size="small" onClick={() => { setAttachment(null); setAttachmentError(""); }} sx={{ flexShrink: 0 }}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#9CA3AF', flexShrink: 0, whiteSpace: 'nowrap' }}>
                          WEBP, JPG, JPEG, PDF • max 5MB
                        </Typography>
                      )}
                    </Box>
                    {attachmentError && (
                      <Typography variant="caption" sx={{ color: '#EF4444', fontWeight: '500' }}>
                        {attachmentError}
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </DialogContent>

          <div style={{ borderTop: '1px solid #E5E7EB', margin: '0 24px' }}></div>

          {/* Actions */}
          <DialogActions sx={{ p: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="text"
              onClick={handleClose}
              sx={{
                color: '#4B5563',
                textTransform: 'none',
                fontWeight: '600',
                px: 2,
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                }
              }}
            >
              Cancel
            </Button>
            {feedbackType !== 'concern' && (
              <Button
                variant="contained"
                type="submit"
                disabled={isLoading || !category || !feedback || (category === 'others' && !otherFeedback)}
                sx={{
                  backgroundColor: '#4F46E5',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: '600',
                  borderRadius: '8px',
                  px: 3.5,
                  py: 1.25,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#4338CA',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#E5E7EB',
                    color: '#9CA3AF',
                  }
                }}
              >
                Submit Feedback
              </Button>
            )}
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default ShareFeedback;